import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import type { PersistConfig } from 'redux-persist';

import { hasProperty } from '@skynet/type-guards';

import type { BaseFilterOptions, FilterOptions, PersistOptions } from './types';

/** The redux-persist storage adapter the factory persists through. */
export type FilterOptionsStorage = PersistConfig<unknown>['storage'];

export interface FilterOptionsSliceParams<T> {
  /** Unique prefix for the action names. */
  prefix: string;
  initialState: T;
  sortDefault?: string;
  /** State applied on `resetFilterOptions`, alongside clearing `search` and resetting `sort`. */
  clearState: T & Partial<BaseFilterOptions>;
}

export interface PersistedFilterOptionsSliceParams<T> extends FilterOptionsSliceParams<T> {
  /** Persist the filter options under `${prefix}/filter`. */
  optionsPersist: PersistOptions;
  /**
   * Storage to persist through. Required so the app's own adapter, which decides
   * which keys survive a purge, cannot be bypassed by omission.
   */
  storage: FilterOptionsStorage;
}

export const createFilterOptionsSlice = <T>({
  prefix,
  initialState,
  sortDefault,
  clearState,
}: FilterOptionsSliceParams<T>) => {
  const initialValue = {
    ...initialState,
    sort: sortDefault ?? '',
    search: '',
  } as FilterOptions<T>;

  return createSlice({
    name: `${prefix}/filterOptions`,
    initialState: initialValue,
    reducers: {
      setFilterOptions: (state, action: PayloadAction<Partial<FilterOptions<T>>>) => {
        const validPayload = Object.fromEntries(
          Object.entries(action.payload).filter(([key, value]) => {
            // Check if key exists in state
            if (!hasProperty(state, key)) {
              return false;
            }

            const stateValue = state[key as keyof typeof state];

            // Both null/undefined - allow only if types match
            if (value === null || value === undefined) {
              return stateValue === null || stateValue === undefined;
            }

            // State value is null/undefined but new value is not - reject
            if (stateValue === null || stateValue === undefined) {
              return false;
            }

            // Check arrays separately
            if (Array.isArray(stateValue)) {
              return Array.isArray(value);
            }

            if (Array.isArray(value)) {
              return false;
            }

            // Check type compatibility for primitives and objects
            return typeof value === typeof stateValue;
          }),
        );
        Object.assign(state, validPayload);
      },
      resetFilterOptions: (state) => {
        // Always reset base filter fields first so search/sort are cleared even if clearState omits them.
        Object.assign(state, { sort: sortDefault ?? '', search: '' }, clearState);
      },
    },
  });
};

export const createPersistedFilterOptionsSlice = <T>({
  optionsPersist,
  storage,
  ...params
}: PersistedFilterOptionsSliceParams<T>) => {
  const slice = createFilterOptionsSlice(params);

  const persistConfig = {
    key: `${params.prefix}/filter`,
    storage,
    blacklist: ['sort', 'search', ...(optionsPersist.blacklist ?? [])],
    ...(optionsPersist.whitelist !== undefined && {
      whitelist: optionsPersist.whitelist,
    }),
  };

  return {
    actions: slice.actions,
    reducer: persistReducer(persistConfig, slice.reducer),
  };
};
